import { Injectable, inject } from "@angular/core";

import { Actions, createEffect, ofType } from "@ngrx/effects";

import { of } from 'rxjs';
import { map, mergeMap, catchError, switchMap } from 'rxjs/operators';

import { ProductContainerActions } from "@core/state/product-containers/product-containers.actions";
import { ProductsDataService } from "@core/services/datasources/products-data.service";
import { ProductContainer } from "@shared/models";
import { createNormalizedError } from "@core/utilities/error-handler.util";


@Injectable({ providedIn: 'root' })
export class ProductContainersEffects {

  private readonly actions$ = inject(Actions);
  private readonly productsDataService = inject(ProductsDataService);

  constructor() { }

  load$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductContainerActions.load),
      mergeMap(() => this.productsDataService.getProductContainers(true)
        .pipe(
          map(productContainers => {
            const containers = productContainers.map(pc => new ProductContainer(pc));
            return ProductContainerActions.loadSuccess({ productContainers: containers });
          }),
          catchError(error => {
            const normalizedError = createNormalizedError(error);
            return of(ProductContainerActions.loadFailure({ error: normalizedError }));
          })
        )
      )
    )
  });

  add$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductContainerActions.add),
      map((action) => action.command),
      mergeMap((command) => this.productsDataService.addProductContainer(command)
        .pipe(
          map(response => {
            const productContainer = new ProductContainer(response);
            return ProductContainerActions.addSuccess({ productContainer });
          }),
          catchError(error => {
            const normalizedError = createNormalizedError(error);
            return of(ProductContainerActions.addFailure({ error: normalizedError }));
          })
        )
      )
    )
  });

  deActivate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductContainerActions.deActivate),
      map((action) => action.command),
      mergeMap((command) => this.productsDataService.deActivateProductContainer(command)
        .pipe(
          map(response => {
            const productContainer = new ProductContainer(response);
            return ProductContainerActions.deActivateSuccess({ productContainer });
          }),
          catchError(error => {
            const normalizedError = createNormalizedError(error);
            return of(ProductContainerActions.deActivateFailure({ error: normalizedError }));
          })
        )
      )
    )
  });

  update$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductContainerActions.update),
      switchMap((action) => 
        // First deactivate the old product container
        this.productsDataService.deActivateProductContainer(action.deactivateCommand)
          .pipe(
            // Then add the new one
            switchMap(() => 
              this.productsDataService.addProductContainer(action.addCommand)
                .pipe(
                  map(response => {
                    const productContainer = new ProductContainer(response);
                    return ProductContainerActions.updateSuccess({ productContainer });
                  }),
                  catchError(error => {
                    const normalizedError = createNormalizedError(error);
                    return of(ProductContainerActions.updateFailure({ error: normalizedError }));
                  })
                )
            ),
            catchError(error => {
              const normalizedError = createNormalizedError(error);
              return of(ProductContainerActions.updateFailure({ error: normalizedError }));
            })
          )
      )
    )
  });
}